import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import LockIcon from "@mui/icons-material/Lock";
import Tooltip from "@mui/material/Tooltip";
import type { KeyboardEvent, MouseEvent } from "react";
import { paygTooltipCopy } from "@/constants/review";

/**
 * The "Activate 'pay-as-you-give' billing" switch — a Pro/Enterprise feature.
 * Mirrors the prototype's `.checkout-payg-row` / `#paygRow`
 * (Campaign-Builder-Master-Prototype-Stage2-Revisions.html, markup ~line 11028,
 * CSS ~line 7880, `onPaygToggle()` ~line 23837, `renderPaygRow()` ~line 23856).
 *
 * The whole row is the switch (`role="switch"`, as in the prototype) and the
 * knob is decorative, so there is no nested control to tab past. On the free
 * tier the row is `locked`: it shows the UPGRADE badge and every activation
 * path — row click, Enter/Space, the badge itself — routes to the upgrade
 * modal instead of flipping the state.
 *
 * TWO DIFFERENT REFUSALS, and only the first is this component's. `locked` is
 * the PLAN gate, which the row can answer on its own because it is a property
 * of the client. The campaign's SIZE is not: pay-as-you-give has a minimum, and
 * whether this campaign clears it is the checkout column's to know — so the row
 * always reports the activation and renders whatever `notice` comes back. The
 * row is never hidden or dimmed for size: a client who cannot see the switch
 * cannot learn the feature exists (Jayanth, 2026-09-23).
 *
 * The info tooltip is MUI's rather than the prototype's `::after` popover, so
 * it is dismissible with Escape and positioned inside the viewport.
 */
export interface PaygRowProps {
  checked: boolean;
  /** The organisation's pay-as-you-give deposit percentage, for the tooltip. */
  minUpfrontPc: number;
  /** True on the free tier — the switch reads as a gated affordance. */
  locked: boolean;
  /**
   * Never called while `locked`. May be DECLINED by the caller — an activation on a campaign under
   * the pay-as-you-give minimum comes back as a `notice` with `checked` unchanged.
   */
  onChange: (next: boolean) => void;
  /**
   * Why the last activation was refused, or why the switch reverted to prepaid on its own. Rendered
   * under the row, outside the switch, so reading it cannot toggle anything.
   */
  notice?: string | null;
  onUpgrade: () => void;
}

export default function PaygRow({
  checked,
  minUpfrontPc,
  locked,
  onChange,
  onUpgrade,
  notice,
}: PaygRowProps) {
  function activate() {
    if (locked) {
      onUpgrade();
      return;
    }
    onChange(!checked);
  }

  function handleKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      activate();
    }
  }

  function handleBadgeClick(event: MouseEvent<HTMLButtonElement>) {
    event.stopPropagation();
    onUpgrade();
  }

  return (
    <>
      <div
        role="switch"
        aria-checked={checked}
        aria-label="Activate pay-as-you-give billing"
        tabIndex={0}
        onClick={activate}
        onKeyDown={handleKeyDown}
        className="mt-3.5 flex cursor-pointer items-center justify-between gap-3 rounded-card border border-card-border bg-clear-dust px-3.5 py-3 font-lato text-[13px] text-rodeo-black"
      >
        <span className="inline-flex items-center gap-2 leading-[1.35] font-semibold">
          {/* Reads as an invitation while off and as a statement of fact once on (Judd,
              2026-08-26) — the figures above it have already changed, so "Activate" would be
              describing something that has happened. */}
          {checked
            ? "'Pay-as-you-give' billing is activated"
            : "Activate 'pay-as-you-give' billing"}
          <Tooltip title={paygTooltipCopy(minUpfrontPc)} enterTouchDelay={0} arrow>
            <InfoOutlinedIcon
              className="shrink-0 cursor-help text-graphite"
              sx={{ fontSize: 16 }}
              aria-label="About pay-as-you-give billing"
              role="img"
              tabIndex={0}
              onClick={(event: MouseEvent<SVGSVGElement>) => event.stopPropagation()}
            />
          </Tooltip>
          {locked && (
            <button
              type="button"
              onClick={handleBadgeClick}
              className="inline-flex shrink-0 items-center gap-1 rounded-pill bg-light-dust px-2 py-0.5 font-lato text-[10px] font-bold tracking-wide text-dust-ink uppercase"
            >
              <LockIcon sx={{ fontSize: 12 }} aria-hidden="true" />
              Upgrade
            </button>
          )}
        </span>

        {/* Decorative knob — the row itself carries the switch semantics. */}
        <span
          aria-hidden="true"
          className={`relative h-[22px] w-10 shrink-0 rounded-full transition-colors duration-200 ${
            checked ? "bg-dark-dust" : "bg-light-dust"
          } ${locked ? "opacity-55" : ""}`}
        >
          <span
            className={`absolute top-0.5 left-0.5 h-[18px] w-[18px] rounded-full bg-white shadow transition-transform duration-200 ${
              checked ? "translate-x-[18px]" : "translate-x-0"
            }`}
          />
        </span>
      </div>

      {/* `role="alert"` because it answers a click the user just made — and because the same line
          is how a campaign that shrank learns its billing went back to prepaid. */}
      {notice && (
        <p
          role="alert"
          className="mt-2 mb-0 font-lato text-[12px] font-semibold text-good-wine leading-[1.4]"
        >
          {notice}
        </p>
      )}
    </>
  );
}
